import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Plane, Clock, Shield, Check, ArrowRight, Globe, Zap, Tag } from "lucide-react";
import { useEffect } from "react";

const bookingFeatures = [
  { icon: Globe, title: "Global Coverage", desc: "Access to 500+ airlines worldwide" },
  { icon: Tag, title: "Best Price Guarantee", desc: "Competitive fares with price matching" },
  { icon: Zap, title: "Quick Booking", desc: "Simple, fast booking process" },
  { icon: Shield, title: "Secure Payments", desc: "100% secure transactions" },
];

const cabinClasses = [
  {
    name: "Economy Class",
    desc: "Comfortable travel at the best value",
    features: ["Standard seat pitch", "Complimentary snacks", "In-flight entertainment"],
    image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600&q=80"
  },
  {
    name: "Premium Economy",
    desc: "Extra comfort for longer journeys",
    features: ["Extra legroom", "Priority boarding", "Enhanced meal service"],
    image: "https://images.unsplash.com/photo-1540339832862-474599807836?w=600&q=80"
  },
  {
    name: "Business Class",
    desc: "Premium experience from start to finish",
    features: ["Lie-flat seats", "Lounge access", "Gourmet dining"],
    image: "https://images.unsplash.com/photo-1587019158091-1a103c5dd17f?w=600&q=80"
  },
];

export default function FlightBookingsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white" data-testid="flight-bookings-page">
      <SeoHead
        title="International Flight Booking Assistance"
        description="Book international flights with confidence. Tripsyfy helps compare routes, timing, airlines, and travel needs with human support."
        canonical="https://tripsyfy.com/services/flight-bookings"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-accent text-sm font-semibold tracking-wide uppercase mb-4">Our Services</p>
              <h1 className="heading-xl text-primary mb-6">Flight Bookings</h1>
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                Book flights to any destination worldwide with our expert assistance. We find the best routes, prices, and schedules tailored to your travel needs.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {bookingFeatures.map((feature, i) => (
                <div key={feature.title} className="bg-white p-6 rounded-lg border border-border text-center">
                  <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-bold text-primary mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Cabin Classes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="heading-lg text-primary mb-4">Choose Your Cabin Class</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                From economy to business, we help you find the perfect travel experience for your budget and comfort preferences.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {cabinClasses.map((cabin, i) => (
              <AnimatedSection key={cabin.name} delay={i * 0.1}>
                <div className="bg-white border border-border rounded-lg overflow-hidden hover:border-accent/30 transition-colors">
                  <div className="h-40 overflow-hidden">
                    <img src={cabin.image} alt={cabin.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-primary mb-1">{cabin.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{cabin.desc}</p>
                    <ul className="space-y-2">
                      {cabin.features.map((feature, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm">
                          <Check className="h-4 w-4 text-accent" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="heading-lg text-primary mb-4">How Flight Booking Works</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
                <h3 className="font-bold text-primary mb-2">Tell Us Your Plans</h3>
                <p className="text-sm text-muted-foreground">Share your destination, dates, and preferences</p>
              </div>
              <div className="text-center">
                <div className="h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
                <h3 className="font-bold text-primary mb-2">Get Custom Options</h3>
                <p className="text-sm text-muted-foreground">Receive curated flight options with best prices</p>
              </div>
              <div className="text-center">
                <div className="h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
                <h3 className="font-bold text-primary mb-2">Book & Fly</h3>
                <p className="text-sm text-muted-foreground">Confirm your booking and get ready to travel</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-lg text-primary-foreground mb-4">Ready to Book Your Flight?</h2>
            <p className="text-primary-foreground/70 mb-8 max-w-xl mx-auto">
              Let us find the best flight deals for your next trip. Get a personalized quote today.
            </p>
            <Link to="/contact">
              <Button variant="navySolid" size="xl" className="gap-2">
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
